import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import thesis_pages from './doc_thesis';

import './docs.css';

import { PageWrapper, DocPageHeading, DocSectionHeading, Paragraph, Columns, Title, SuperTitle, FancyLink } from './components';


const Page = ({ page, next, prev, baseUrl, backToText, backToUrl }) => {
    return <div>
        <DocPageHeading>{ page.title }</DocPageHeading>
        <ContentElements elements={ page.elements } baseUrl={ baseUrl } />
        <div className='docs-bottom-row'>
            <div className='docs-bottom-left'>{
                prev ?
                <FancyLink to={`${baseUrl}/${prev.id}`} direction='left'>{ prev.title }</FancyLink> :
                null
            }</div>
            <div className='docs-bottom-right'>{
                next ?
                <FancyLink to={`${baseUrl}/${next.id}`} direction='right'>{ next.title }</FancyLink> :
                null
            }</div>
        </div>
        <div>{
            backToText ?
            <FancyLink to={ backToUrl } direction='left'>Back to { backToText }</FancyLink> :
            null
        }</div>
    </div>
}

const ContentElements = ({ elements, baseUrl }) => {
    return <div>
        { elements.map((element, i) => <Content key={ i } element={ element } baseUrl={ baseUrl } />) }
    </div>
}

const boldPrefix = (text, baseUrl) => {
    const parts = text.split(':', 2);
    return <><b>{ parts[0] }</b>: { textWithLinks(parts[1], baseUrl)}</>
}

function textWithLinks(text, baseUrl) {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      const link = match[2];
      const externalLink = link.includes(':');

      const fullUrl = link.startsWith('/') ? link : `${baseUrl}/${link}`

      parts.push(
        <Link
          key={match.index}
          to={ externalLink ? link : fullUrl}
          target={ externalLink ? "_blank" : null }
          rel={ externalLink ? "noopener noreferrer" : null }
        >
          {match[1]}
        </Link>
      );

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length ? parts : text;
}


const Content = ({ element, baseUrl }) => {

    switch (element.type) {
        case 'paragraph':
            return <Paragraph>{ textWithLinks(element.text, baseUrl) }</Paragraph>

        case 'image':
            return <Paragraph>
                <div className='docs-image'><img
                    src={ `/static/${element.path}` }
                    width={ element.width }
                    height={ element.height }
                    alt={ element.descr }
                /></div>
                <div className='docs-caption'>{ element.caption }</div>
            </Paragraph>

        case 'heading':
            return <DocSectionHeading>{ element.title }</DocSectionHeading>

        case 'bullets':
            return <div className='docs-bullets'>
                { element.bullets.map((b, i) => (
                    b.includes(':') ?
                    <div key={ i } className='docs-bullet'>{ boldPrefix(b, baseUrl) }</div> :
                    <div key={ i } className='docs-bullet'>{ textWithLinks(b, baseUrl) }</div>
                ))}
            </div>

        case 'cols':
            return <Columns>
                { element.cols.map((col, i) => <ContentElements key={ i } elements={ col } baseUrl={ baseUrl } />)}
            </Columns>

        default:
            return <div>Something else...</div>
    }
}

const Toc = ({ pages, pageId, baseUrl }) => {
    return <div className='docs-toc-outer'>
        {/* <div className='sans-heading bottom-small'>{ title }</div> */}
        <div className='docs-toc-inner'>
            { pages.map((page) => <Link className='sans-heading' key={ page.id } to={`${baseUrl}/${page.id}`} ><div
                className={ pageId === page.id ? 'docs-toc-page-current' : 'docs-toc-page' }
            >{ page.title }</div></Link>)}
        </div>
    </div>
}

const Doc = ({ pageId, title, subtitle, pages, baseUrl, backToUrl }) => {
    const { page } = useParams();

    const actualPageId = pageId || page || pages[0].id;

    const pageDict = useMemo(() => {
        return Object.fromEntries(pages.map((p, i) => [p.id, {
            page: p,
            next: i < pages.length ? pages[i + 1] : null,
            prev: i > 0 ? pages[i - 1] : null,
        }]));
    }, [ pages ]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [actualPageId]);

    const contents = <Toc
        title={ title }
        pages={ pages }
        pageId={ actualPageId }
        baseUrl={ baseUrl }
    />

    return <PageWrapper sidebar={ contents }>
        {
            backToUrl ?
            <Link to={ backToUrl}><SuperTitle>{ subtitle }</SuperTitle></Link> :
            <SuperTitle>{ subtitle }</SuperTitle>
        }
        <Title>{ title}</Title>
        <Page
            page={ pageDict[actualPageId].page }
            next={ pageDict[actualPageId].next }
            prev={ pageDict[actualPageId].prev }
            baseUrl={ baseUrl }
            backToText={ subtitle }
            backToUrl={ backToUrl }
        />
    </PageWrapper>
}

export default Doc;

export const ThesisPages = () => {
    const { pageId } = useParams();

    return <Doc
        title='The new relationship'
        subtitle={ 'Merelogic\'s Thesis' }
        pages={ thesis_pages }
        baseUrl={ '/thesis/' }
        pageId={ pageId }
    />
}
